import {
  ContextMenu,
  DataTree,
  DataTreeProps,
  Separator,
} from '@infotech-soe/erp-ui-kit';
import { TDatabaseId } from '@infotech-soe/metaschema';
import React, { memo, MouseEvent, useCallback, useMemo } from 'react';

import { CategoryTreeType } from '../../../services/category/view-model/CategoryViewModel';

import { HierarchyTreeConnectorProps } from './HierarchyTreeContainer';
import TreeTitledContainer from './TreeTitledContainer';
import { useTree } from './useTree';
import {
  SEPARATOR,
  TreeCommand,
  TreeCommandName,
  useTreeCommands,
} from './useTreeCommands';
import { useTreeContextMenu } from './useTreeContextMenu';
import { findNode } from './utils';

export interface HierarchyTreeProps
  extends
    HierarchyTreeConnectorProps,
    Pick<DataTreeProps<TDatabaseId>, 'className' | 'style' | 'hideCheckbox'> {
  allowRefresh?: boolean;
  hideCommands?: boolean;
}

const HierarchyTree = (props: HierarchyTreeProps): React.JSX.Element => {
  const {
    hideCheckbox,
    hideCommands,
    allowRefresh,
    title = '',
    tree,
    onCheckToggle,
    onClearChecked,
    onMove: _onMove,
    onShowConfirmation,
    onRefresh,
    onSetHierarchy,
    onSelect,
    ...restDataTreeProps
  } = props;
  const { activeIndex, handle } = useTree(tree);
  const { isOpen, setOpen, onContextMenu, positionX, positionY } =
    useTreeContextMenu({ selectNode: onSelect });
  const handleMove = useCallback(
    (nodeId: TDatabaseId | string, newParentId: TDatabaseId) => {
      if (typeof nodeId !== 'string') return;

      const { ids } = JSON.parse(nodeId) as { ids: TDatabaseId[] };
      const movingToTheSamePlace =
        (!tree.checkedNodes.size && tree.activeId === newParentId) ||
        (tree.checkedNodes.size === 1 && tree.checkedNodes.has(newParentId)) ||
        ids.includes(newParentId);
      const movingIntoChildren = ids.some(id =>
        findNode(tree.nodes, newParentId)?.nodePath.has(id)
      );

      if (movingToTheSamePlace || movingIntoChildren) return;

      onShowConfirmation(ids, newParentId);
    },
    [onShowConfirmation, tree]
  );

  const handleCheckToggle = useCallback(
    (e: MouseEvent<HTMLDivElement> | KeyboardEvent, id?: TDatabaseId) => {
      onCheckToggle(id, e.shiftKey);
    },
    [onCheckToggle]
  );

  const { commands } = useTreeCommands({
    treeType: CategoryTreeType.HIERARCHY,
    activeNode: tree.nodes[activeIndex],
    tree,
    hideCommands,
    onCheckToggle,
    onClearChecked,
    onRefresh,
    onSetHierarchy,
  });

  const options = useMemo<Array<TreeCommand | Separator> | undefined>(
    () =>
      commands
        ? [
            ...(hideCheckbox
              ? []
              : [
                  commands[TreeCommandName.HIERARCHY],
                  commands[TreeCommandName.CLEAR],
                  commands[TreeCommandName.TOGGLE],
                  SEPARATOR,
                ]),
            commands[TreeCommandName.REFRESH],
          ].filter(cmd => !!cmd)
        : undefined,
    [commands, hideCheckbox]
  );

  return (
    <TreeTitledContainer
      commands={options}
      hideCommands={hideCommands}
      title={title}
    >
      <DataTree
        activeId={tree.activeId}
        data={tree.nodes}
        draggable
        checked={tree.checkedNodes}
        hierarchyId={tree.hierarchyRootId}
        expanded={tree.expandedNodes}
        pending={tree.pendingNodes}
        ref={handle}
        hideCheckbox={hideCheckbox}
        onCheckToggle={handleCheckToggle}
        onClearChecked={onClearChecked}
        onContextMenu={onContextMenu}
        onMove={handleMove}
        onRefresh={onRefresh}
        onSelect={onSelect}
        onSetHierarchy={onSetHierarchy}
        {...restDataTreeProps}
      />
      <ContextMenu
        open={activeIndex !== -1 && isOpen && !hideCommands}
        options={options}
        x={positionX}
        y={positionY}
        onClick={(commandName: string) => commands?.[commandName].handler()}
        onOpen={setOpen}
      />
    </TreeTitledContainer>
  );
};

export default memo(HierarchyTree);
